/**
 * Officer's caseload — thin wrapper around dars-service cases routes.
 *
 * `list()` uses `assigned_officer_id=me` (dars-service resolves to caller's
 * user_id + view_own scoping applies). `create()` posts to /api/v1/dars/cases;
 * the server always auto-generates `case_number` via dars.case_sequences —
 * client should NOT send one. `getDetail()` hits the aggregate GET which
 * returns participants + notes inline.
 */
import { api } from '@adaptcx/shared/services/api'

export interface OfficerCase {
  id: string
  case_number: string
  case_type: string
  status: string
  priority?: string | null
  title?: string | null
  description?: string | null
  filed_at?: string | null
  closed_at?: string | null
  assigned_officer_id?: string | null
  issue_code?: string | null
  issue_description?: string | null
  determination_number?: string | null
  determination_date?: string | null
  determination_description?: string | null
  is_sealed?: boolean
  legal_hold?: boolean
}

export interface OfficerCaseListResponse {
  items: OfficerCase[]
  total: number
}

export interface ParticipantPhone {
  type: 'mobile' | 'home' | 'work' | string
  number: string
  extension?: string | null
  is_primary?: boolean
}

export interface OfficerCaseParticipant {
  id: string
  case_id: string
  participant_type: string
  first_name: string
  last_name: string
  email?: string | null
  phone?: string | null
  phones?: ParticipantPhone[] | null
  organization?: string | null
  address?: string | null
  city?: string | null
  state?: string | null
  zip_code?: string | null
  notes?: string | null
  is_primary?: boolean
  contact_id?: string | null
  user_id?: string | null
  created_at?: string
  updated_at?: string
}

export interface OfficerCaseParticipantCreate {
  participant_type: string
  first_name: string
  last_name: string
  email?: string
  phone?: string
  phones?: ParticipantPhone[]
  organization?: string
  address?: string
  city?: string
  state?: string
  zip_code?: string
  is_primary?: boolean
  notes?: string
}

export interface OfficerCaseNote {
  id: string
  case_id: string
  content: string
  note_type?: string
  author_name?: string | null
  author_id?: string | null
  created_at: string
  updated_at?: string
}

export interface OfficerCaseDetail extends OfficerCase {
  participants: OfficerCaseParticipant[]
  notes: OfficerCaseNote[]
}

export interface CaseCreateBody {
  case_type: string
  title?: string
  description?: string
  priority?: string
  assigned_officer_id?: string | null
  issue_code?: string
  issue_description?: string
  determination_number?: string
  determination_date?: string
  determination_description?: string
}

export interface CaseUpdateBody extends Partial<CaseCreateBody> {
  status?: string
}

export const officerCasesService = {
  async list(params?: {
    status?: string
    case_type?: string
    limit?: number
    offset?: number
    /** When false, the `assigned_officer_id=me` filter is dropped and the
     * backend scopes results by the caller's cases.view_own vs .view_all
     * permission. Used by the schedule-hearing case picker so officers
     * with view_all see every case in the tenant. Defaults to true. */
    mine?: boolean
  }): Promise<OfficerCaseListResponse> {
    const { mine, ...rest } = params || {}
    const q: Record<string, unknown> = { ...rest }
    if (mine !== false) q.assigned_officer_id = 'me'
    const { data } = await api.get<OfficerCaseListResponse>('/api/v1/dars/cases', { params: q })
    return data
  },

  async get(caseId: string): Promise<OfficerCase> {
    const { data } = await api.get<OfficerCase>(`/api/v1/dars/cases/${caseId}`)
    return data
  },

  async getDetail(caseId: string): Promise<OfficerCaseDetail> {
    const { data } = await api.get<OfficerCaseDetail>(`/api/v1/dars/cases/${caseId}`)
    return data
  },

  async create(body: CaseCreateBody): Promise<OfficerCase> {
    const { data } = await api.post<OfficerCase>('/api/v1/dars/cases', body)
    return data
  },

  async update(caseId: string, body: CaseUpdateBody): Promise<OfficerCase> {
    const { data } = await api.patch<OfficerCase>(`/api/v1/dars/cases/${caseId}`, body)
    return data
  },

  // ── Participants (nested under a case) ────────────────────────
  async listParticipants(caseId: string): Promise<OfficerCaseParticipant[]> {
    // Backend returns participants inline on the case-detail GET;
    // re-hitting that endpoint is idempotent and cheap for refresh.
    const detail = await api.get<OfficerCaseDetail>(`/api/v1/dars/cases/${caseId}`)
    return detail.data.participants || []
  },
  async addParticipant(
    caseId: string,
    body: OfficerCaseParticipantCreate,
  ): Promise<OfficerCaseParticipant> {
    const { data } = await api.post<OfficerCaseParticipant>(
      `/api/v1/dars/cases/${caseId}/participants`,
      body,
    )
    return data
  },
  async updateParticipant(
    caseId: string,
    participantId: string,
    body: Partial<OfficerCaseParticipantCreate>,
  ): Promise<OfficerCaseParticipant> {
    const { data } = await api.patch<OfficerCaseParticipant>(
      `/api/v1/dars/cases/${caseId}/participants/${participantId}`,
      body,
    )
    return data
  },
  async removeParticipant(caseId: string, participantId: string): Promise<void> {
    await api.delete(`/api/v1/dars/cases/${caseId}/participants/${participantId}`)
  },

  // ── Notes (nested under a case) ───────────────────────────────
  async listNotes(caseId: string): Promise<OfficerCaseNote[]> {
    const detail = await api.get<OfficerCaseDetail>(`/api/v1/dars/cases/${caseId}`)
    return (detail.data.notes as OfficerCaseNote[]) || []
  },
  async addNote(caseId: string, body: { content: string; note_type?: string }): Promise<OfficerCaseNote> {
    const { data } = await api.post<OfficerCaseNote>(
      `/api/v1/dars/cases/${caseId}/notes`,
      body,
    )
    return data
  },
  async deleteNote(caseId: string, noteId: string): Promise<void> {
    await api.delete(`/api/v1/dars/cases/${caseId}/notes/${noteId}`)
  },
}
